/**
 * GA4 Analytics Service mit Consent-Gating.
 *
 * Verantwortlich für:
 * - Consent-State (LocalStorage `monemee_consent_v1`)
 * - Lazy-Load von gtag.js NACH Consent
 * - Event-Queue: Track-Calls vor Consent werden gepuffert und nach Grant geflushed
 * - Manuelle Pageviews (kein send_page_view; Hook übernimmt)
 * - User-Id-Hashing via SHA-256
 *
 * NICHT direkt importieren in dem Sinn, dass gtag-Calls ohne Consent feuern —
 * der Service entscheidet das.
 */

const CONSENT_KEY = 'monemee_consent_v1';
const MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || '';

let scriptLoaded = false;
const eventQueue = [];

function getGtag() {
  if (typeof window === 'undefined') return null;
  return window.gtag || null;
}

function pushGtag() {
  // window.gtag wird im index.html-Inline-Script definiert, ist also stets verfügbar.
  const fn = getGtag();
  if (fn) fn.apply(null, arguments);
}

export function getConsent() {
  if (typeof window === 'undefined') return { analytics: false };
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return { analytics: false };
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : { analytics: false };
  } catch (_) {
    return { analytics: false };
  }
}

export function hasDecision() {
  const c = getConsent();
  return typeof c.ts === 'number';
}

function loadGtagScript() {
  if (scriptLoaded || typeof document === 'undefined' || !MEASUREMENT_ID) return;
  scriptLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);
  // Manuelle Pageviews via useAnalytics-Hook
  pushGtag('config', MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false
  });
}

function flushQueue() {
  while (eventQueue.length) {
    const ev = eventQueue.shift();
    pushGtag('event', ev.name, ev.params || {});
  }
}

export function setConsent({ analytics }) {
  if (typeof window === 'undefined') return;
  const payload = { analytics: !!analytics, ts: Date.now() };
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  } catch (_) {
    // ignore — Safari Private Mode etc.
  }
  pushGtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied'
  });
  if (analytics) {
    loadGtagScript();
    flushQueue();
  }
}

export function track(name, params = {}) {
  const { analytics } = getConsent();
  if (!analytics) {
    eventQueue.push({ name, params });
    return;
  }
  pushGtag('event', name, params);
}

export function setUserProperties(props) {
  if (!getConsent().analytics) return;
  pushGtag('set', 'user_properties', props || {});
}

async function sha256Hex(input) {
  if (typeof window === 'undefined' || !window.crypto || !window.crypto.subtle) {
    return null;
  }
  const enc = new TextEncoder().encode(String(input));
  const buf = await window.crypto.subtle.digest('SHA-256', enc);
  const bytes = new Uint8Array(buf);
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}

export async function setUserId(rawUid) {
  if (!getConsent().analytics) return;
  if (!rawUid) {
    pushGtag('config', MEASUREMENT_ID, { user_id: null });
    return;
  }
  const hashed = await sha256Hex(rawUid);
  if (!hashed) return;
  pushGtag('config', MEASUREMENT_ID, { user_id: hashed });
}

// Test-Hook: erlaubt Reset zwischen Tests
export function __resetForTests() {
  scriptLoaded = false;
  eventQueue.length = 0;
}

const analytics = {
  getConsent,
  hasDecision,
  setConsent,
  track,
  setUserProperties,
  setUserId
};

export default analytics;
